// AppBreadcrumb.tsx
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import { Breadcrumb } from 'antd';
import { ReactComponent as BreadcrumbIcon } from '../../images/Icon-breadcrumb-arrow.svg';
import { ReactComponent as HomeIconFilled } from '../../images/icon-home.svg';
import { useTranslation } from '../../i18n/useTranslation';
import { postMessageAction, postMessageService } from '../shared/postMessageService';
import { PageRoutes } from '../../conf/Global';

interface BreadCrumbItem {
    name: string;
    path: string;
}

interface AppBreadcrumbProps {
    breadcrumbs: BreadCrumbItem[];
}

export const BreadcrumbItem = styled(Breadcrumb.Item)<{ disabled?: boolean }>`
    /* Remove the color change on hover, only change cursor */
    &&& :hover {
        color: ${(props) => props.theme.styles['blue-s100']};
        cursor: pointer;
    }
`;

const StyledButton = styled.button<{ disabled?: boolean }>`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: inherit;
    font-weight: normal;
    font-size: inherit;
    font-family: inherit;

    &:hover {
        color: inherit !important;
        background: none;
    }

    &:focus {
        outline: none;
        color: inherit;
    }
`;

const DisplayNoneBreadcrumbIcon = styled(BreadcrumbIcon)`
    display: none;
`;

export const IS_BREADCRUMB_HIDDEN_PARAM = 'isBreadcrumbHidden';

export const AppBreadcrumb = ({ breadcrumbs }: AppBreadcrumbProps) => {
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();

    // Check if breadcrumbs should be hidden based on URL parameter
    const searchParams = new URLSearchParams(location.search);
    const isFromSetting = searchParams.get(IS_BREADCRUMB_HIDDEN_PARAM);

    if (!breadcrumbs?.length || isFromSetting) {
        return null;
    }

    const handleClick = (item: BreadCrumbItem) => {
        // this condition is added to handle the route redirecting of default home and module home from vue side
        if (item.path === PageRoutes.DEFAULT_HOME || item.path === PageRoutes.MODULE_HOME) {
            postMessageService.sendToParent({
                action: postMessageAction.REDIRECT_FROM_BREADCRUMB_VUE_APP,
                path: item.path,
            });
            return;
        }
        history.push(item.path);
    };

    const isItemDisabled = (item: BreadCrumbItem, index: number) => {
        return !item.path || index === breadcrumbs.length - 1 || item.path === location.pathname;
    };

    return (
        <Breadcrumb className="f-page-breadcrumb" separator={<BreadcrumbIcon />}>
            {breadcrumbs.slice(0, -1).map((item, index) => (
                <BreadcrumbItem
                    key={JSON.stringify(item)}
                    className="f-breadcrumb-item"
                    disabled={isItemDisabled(item, index)}
                >
                    <StyledButton
                        type="button"
                        onClick={() => handleClick(item)}
                        disabled={isItemDisabled(item, index)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleClick(item);
                            }
                        }}
                    >
                        {index === 0 ? <HomeIconFilled /> : t(item.name)}
                    </StyledButton>
                </BreadcrumbItem>
            ))}
            <DisplayNoneBreadcrumbIcon />
        </Breadcrumb>
    );
};
